// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { Step } from '@med/app/home/demo/guided-tour/models/step';
import clone from 'clone';
import { Campaign } from '@med/app/home/campaign/model/campaign';
import { Observable, of, zip } from 'rxjs';
import { Store } from '@ngrx/store';
import { concatMap, map } from 'rxjs/operators';
import { CampaignDefault } from '@med/app/shared/models/campaign-defaults/campaign-default';
import { Country } from '@med/app/core/model/country';
import { SpotPlan } from '@med/app/home/campaign/model/spot-plan.model';
import { EstimatesDto } from '@med/app/home/campaign/model/estimates';
import { Demographics } from '@med/app/shared/models/demographics/demographics';
import { PresetTree } from '@med/app/core/services/preset/preset-tree';
import { Client } from '@med/app/auth/model/client';
import { CampaignList } from '@med/app/home/campaign/model/campaign-list';
import { Budget } from '@med/app/home/campaign/model/budget';
import { HierarchicalLocationUnitGroup } from '@med/app/home/campaign/model/hierarchical-location-unit-group';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { AdFormat } from '@med/app/shared/modules/ad-format/model/ad-format.model';
import { Platform } from '@med/app/shared/modules/platform/model/platform';
import { PlannerFullDemoStepEnum } from '@med/app/home/planner-full-demo/planner-full-demo-step.enum';
import { plannerFullDemoSteps } from '@med/app/home/planner-full-demo/planner-full-demo-steps';
import { City } from '../planner-full/models/cities/city';
import { CompetitorBrand } from '../../shared/models/competitor-brands/competitor-brand';

@Injectable({providedIn: 'root'})
export class PlannerFullDemoDataProviderService {

  constructor(
    private store: Store,
    private httpClient: HttpClient,
  ) {
  }

  getRecommendedPlatforms(language: string): Observable<Platform[]> {
    return this.getFile<Platform[]>(`${ language }/recommended-platforms.json`);
  }

  getBudget(): Observable<Budget> {
    return this.getFile<Budget>(`budget.json`);
  }

  getClients(): Observable<Client[]> {
    return this.getFile<Client[]>(`clients.json`);
  }

  getCampaignList(id: PlannerFullDemoStepEnum, language: string): Observable<CampaignList> {
    switch (id) {
      case PlannerFullDemoStepEnum.campaignListExposeCampaign:
        return zip(
          this.getFile<CampaignList>(`${ language }/campaign-list/exposed-campaign-list.json`),
          this.getCampaign(PlannerFullDemoStepEnum.summaryAskToClickSendForReview, language),
        ).pipe(
          concatMap(([campaignList, campaign]) => {
            return of({
              ...campaignList,
              content: [campaign],
            });
          }),
        );
      default:
        return this.getFile(`${ language }/campaign-list/empty-campaign-list.json`);
    }
  }

  getStep(id: PlannerFullDemoStepEnum): Step {
    const step = plannerFullDemoSteps.find(s => s.stepId === id);
    if (!step) {
      return null;
    }

    const clonedStep = clone(step);
    // clonedStep.stepNumber = steps.findIndex(s => s.stepId === step.stepId) + 1;

    return clonedStep;
  }

  getCampaign(id: PlannerFullDemoStepEnum, language: string): Observable<Campaign> {
    return zip(
      this.getFile<Campaign>(`${ language }/campaign/${ this.getBaseCampaignFilename() }`),
      this.getFile<Partial<Campaign>>(`${ language }/campaign/${ this.getCampaignFilename(id) }`),
    ).pipe(
      map(([baseCampaign, campaignStepExtension]) => {
        return {
          ...baseCampaign,
          ...campaignStepExtension,
          ...this.getCampaignDynamicFields(id),
        };
      }),
    );
  }

  getLocationUnitGroups(language: string): Observable<HierarchicalLocationUnitGroup[]> {
    const url = `${ language }/location-unit-groups.json`;

    return this.getFile<HierarchicalLocationUnitGroup[]>(url);
  }

  getCampaignDefaults(): Observable<CampaignDefault[]> {
    const url = `campaign-defaults.json`;

    return this.getFile<CampaignDefault[]>(url);
  }

  getPlatforms(language: string): Observable<Platform[]> {
    const url = `${ language }/platforms.json`;

    return this.getFile<Platform[]>(url);
  }

  getCities(language: string): Observable<City[]> {
    const url = `${ language }/cities.json`;

    return this.getFile<City[]>(url);
  }

  getCompetitorBrands(language: string): Observable<CompetitorBrand[]> {
    const url = `${ language }/competitor-brands.json`;

    return this.getFile<CompetitorBrand[]>(url);
  }

  getSpotplan(language: string): Observable<SpotPlan> {
    const url = `${ language }/spotplan.json`;

    return this.getFile<SpotPlan>(url);
  }

  getAdformats(language: string): Observable<AdFormat[]> {
    const url = `${ language }/adformats.json`;

    return this.getFile<AdFormat[]>(url);
  }

  getEstimates(id: PlannerFullDemoStepEnum): Observable<EstimatesDto> {
    switch (id) {
      case PlannerFullDemoStepEnum.campaignObjectiveSelectNewStoreOpening:
      case PlannerFullDemoStepEnum.plannerStartSelectAudience:
      case PlannerFullDemoStepEnum.audienceShowEstimates:
      case PlannerFullDemoStepEnum.audienceReadSegmentDescriptions:
        return this.getFile('estimates/1.json');
      default:
        return this.getFile('estimates/2.json');
    }
  }

  getDemographics(language: string): Observable<Demographics> {
    const url = `${ language }/demographics.json`;

    return this.getFile<Demographics>(url);
  }

  getCampaignPresets(language: string): Observable<PresetTree> {
    const url = `${ language }/presets.json`;

    return this.getFile<PresetTree>(url);
  }

  getCountries(language: string): Observable<Country[]> {
    const url = `${ language }/countries.json`;

    return this.getFile<Country[]>(url);
  }

  private getBaseCampaignFilename(): string {
    return 'base-campaign.json';
  }

  private getCampaignDynamicFields(id: PlannerFullDemoStepEnum): Partial<Campaign> {
    switch (id) {
      case PlannerFullDemoStepEnum.summaryAskToClickSendForReview:
      case PlannerFullDemoStepEnum.budgetCalculationExplanation:
      case PlannerFullDemoStepEnum.budgetSelectCampaignDuration: {
        const dateFormat = 'YYYY-MM-DD';

        return {
          timeline: {
            from: moment().add(1, 'day').format(dateFormat),
            to: moment().add(15, 'day').format(dateFormat),
          },
        };
      }
      default:
        return {};
    }
  }


  private getCampaignFilename(id: PlannerFullDemoStepEnum): string {
    switch (id) {
      case PlannerFullDemoStepEnum.budgetSelectCampaignDuration:
        return '2.json';
      case PlannerFullDemoStepEnum.budgetCalculationExplanation:
        return '3.json';
      case PlannerFullDemoStepEnum.mediaSearchPlatform:
        return '4.json';
      case PlannerFullDemoStepEnum.sentForReviewAskToClickBackToDashboard:
        return '5.json';
      case PlannerFullDemoStepEnum.summaryAskToClickSendForReview:
        return '6.json';
      case PlannerFullDemoStepEnum.demographicsSelectGender:
        return '14.json';
      case PlannerFullDemoStepEnum.demographicsAskToClickContinue:
        return '9.json';
      case PlannerFullDemoStepEnum.mediaAskToClickContinue:
        return '15.json';
      case PlannerFullDemoStepEnum.audienceShowEstimatesChange:
        return '11.json';
      case PlannerFullDemoStepEnum.audienceExplainAddingLocationCategories:
        return '12.json';
      case PlannerFullDemoStepEnum.audienceShowTimespan:
        return '13.json';
      case PlannerFullDemoStepEnum.spotplanAnalyze:
        return '16.json';
      default:
        return this.getBaseCampaignFilename();
    }
  }

  private getFile<T>(path: string): Observable<T> {
    const url = `${ window.location.origin }/assets/planner-demo/china/${ path }`;

    return this.httpClient.get<T>(url);
  }
}
